'use client';

import { memo, useMemo } from 'react';

import { type Clip } from '@air/api/types';
import { Tooltip } from '@air/primitive-tooltip';
import { formatBytes } from '@air/utilities';

import { MetadataRow } from '~/components/MetadataRow';
import { SectionLabel } from '~/components/SectionLabel';
import { Tag } from '~/components/Tag';
import { VersionBadge } from '~/components/VersionBadge';
import { formatDateTime } from '~/utils/formatDateTime';
import { getClipDisplayName } from '~/utils/getClipDisplayName';

export type AssetMetadataPanelProps = {
  clip: Clip;
};

/**
 * Displays asset metadata in the detail panel.
 * Shows title, description, tags, and file information.
 */
export const AssetMetadataPanel = memo(({ clip }: AssetMetadataPanelProps) => {
  const title = getClipDisplayName(clip);
  const ext = clip.ext?.toUpperCase();
  const size = clip.size ? formatBytes(clip.size) : undefined;

  const displayWidth = clip.darWidth ?? clip.width;
  const displayHeight = clip.darHeight ?? clip.height;
  const dimensions = displayWidth && displayHeight ? `${displayWidth}×${displayHeight}` : undefined;

  const typeInfo = useMemo(() => {
    const parts: string[] = [];
    if (ext) parts.push(ext);
    if (size) parts.push(size);
    if (dimensions) parts.push(dimensions);
    return parts.join(' · ');
  }, [ext, size, dimensions]);

  const tags = useMemo(() => {
    const allTags = [
      ...(clip.tags?.map((tag) => tag.label).filter(Boolean) ?? []),
      ...(clip.smartTags?.map((tag) => tag.label).filter(Boolean) ?? []),
    ];
    return [...new Set(allTags)];
  }, [clip.tags, clip.smartTags]);

  const dateCreated = clip.recordedAt ? formatDateTime(clip.recordedAt) : undefined;
  const dateUploaded = clip.createdAt ? formatDateTime(clip.createdAt) : undefined;

  return (
    <div className="flex flex-col gap-6 overflow-y-auto">
      <div>
        <Tooltip label={title}>
          <h2 className="truncate font-medium text-16 text-neutral-12 leading-normal">{title}</h2>
        </Tooltip>
        <div className="mt-1 flex items-center gap-1.5">
          {clip.version >= 1 && <VersionBadge version={clip.version} />}
          {typeInfo && <span className="font-medium text-12 text-neutral-10 uppercase tracking-wider">{typeInfo}</span>}
        </div>
      </div>

      {clip.description && (
        <div>
          <SectionLabel className="mb-1">Description</SectionLabel>
          <p className="text-14 text-neutral-12 leading-normal">{clip.description}</p>
        </div>
      )}

      {tags.length > 0 && (
        <div>
          <SectionLabel className="mb-2">Tags</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        </div>
      )}

      <div>
        <SectionLabel className="mb-1">File Information</SectionLabel>
        <div className="text-14">
          <MetadataRow label="Date created" value={dateCreated} />
          <MetadataRow label="Uploaded" value={dateUploaded} />
          <MetadataRow label="Uploaded by" value={clip.owner?.ownerName} />
        </div>
      </div>
    </div>
  );
});

AssetMetadataPanel.displayName = 'AssetMetadataPanel';
